import { useMemo } from 'react'
import { Button, Group, MultiSelect, Select, Text } from '@mantine/core'
import { usePortalTarget } from '../../lib/usePortalTarget'
import { classesInView, filtersAnything, type CarFilter, type CarInfo } from '@racedash/shared'

/**
 * Which cars the chart shows.
 *
 * Two controls that read left to right as one sentence: a class, then the cars
 * within it. They intersect, so the car list only ever offers what the class
 * admits — the alternative is a selected car silently vanishing when the class
 * changes, or overriding it, and both are worse than a selection that visibly
 * prunes itself.
 *
 * The count is not decoration. This screen defaults to a subset on a big field,
 * and hiding rows silently is how people stop trusting a chart — so it always
 * says how many of how many, and the way back to all of them is one click.
 */
export default function CarFilterBar({
  cars,
  filter,
  onChange,
  shown,
  onReset,
}: {
  cars: CarInfo[]
  filter: CarFilter
  onChange: (next: CarFilter) => void
  shown: number
  onReset: () => void
}) {
  const classes = useMemo(() => classesInView(cars), [cars])

  const inClass = useMemo(
    () => (filter.klass ? cars.filter((c) => c.klass === filter.klass) : cars),
    [cars, filter.klass],
  )

  /**
   * Ours first, in their own group.
   *
   * The team's cars are what this screen is nearly always about, and hunting
   * for 436 among a hundred numbers is the thing the grouping exists to stop.
   */
  const options = useMemo(() => {
    const ours = inClass.filter((c) => c.ours).map(toOption)
    const rest = inClass.filter((c) => !c.ours).map(toOption)
    const groups = []
    if (ours.length > 0) groups.push({ group: 'Ours', items: ours })
    if (rest.length > 0) groups.push({ group: ours.length > 0 ? 'Everyone else' : 'Cars', items: rest })
    return groups
  }, [inClass])

  // So the dropdowns are visible on a pit wall, not just on a desk.
  const portal = usePortalTarget()

  const ourNumbers = useMemo(() => inClass.filter((c) => c.ours).map((c) => c.number), [inClass])
  const justOurs =
    ourNumbers.length > 0 &&
    filter.cars.length === ourNumbers.length &&
    ourNumbers.every((n) => filter.cars.includes(n))

  return (
    <Group gap="sm" wrap="wrap" align="center" className="stints-filter">
      {classes.length > 1 && (
        <Select
          aria-label="Class"
          size="xs"
          w={150}
          data={[{ value: '', label: 'All classes' }, ...classes.map((c) => ({ value: c, label: c }))]}
          value={filter.klass ?? ''}
          allowDeselect={false}
          onChange={(v) => onChange({ ...filter, klass: v || null })}
          comboboxProps={{ portalProps: { target: portal } }}
        />
      )}

      <MultiSelect
        aria-label="Cars"
        size="xs"
        w={280}
        placeholder={filter.cars.length === 0 ? 'All cars' : undefined}
        data={options}
        value={filter.cars}
        onChange={(v) => onChange({ ...filter, cars: v })}
        searchable
        clearable
        hidePickedOptions
        maxDropdownHeight={280}
        comboboxProps={{ portalProps: { target: portal } }}
      />

      {ourNumbers.length > 0 && !justOurs && (
        <Button
          size="compact-xs"
          variant="subtle"
          color="gray"
          onClick={() => onChange({ ...filter, cars: ourNumbers })}
        >
          Just ours
        </Button>
      )}

      {filtersAnything(filter) && (
        <Button
          size="compact-xs"
          variant="subtle"
          color="gray"
          onClick={() => onChange({ klass: null, cars: [] })}
        >
          Show all {cars.length}
        </Button>
      )}

      <Text size="xs" c="dimmed">
        {shown} of {cars.length} cars
      </Text>

      {/* Only offered once the filter has been moved away from what the screen
          would have chosen, so it is a way back rather than a permanent
          control nobody needs. */}
      {filtersAnything(filter) && (
        <Button size="compact-xs" variant="subtle" color="gray" onClick={onReset}>
          Reset
        </Button>
      )}
    </Group>
  )
}

function toOption(c: CarInfo) {
  return { value: c.number, label: c.klass ? `${c.number} — ${c.klass}` : c.number }
}
